import { computed, type ComputedRef } from "vue";
import {
  SQDCActionRevisionFieldType,
  SQDCEventActionTypeFieldType,
  SQDCEventRevisionFieldType,
  SQDCEventTypeFieldRelation,
  useEABRevisionLinkedEventsQuery,
  useGetCostCentersForRainbowTileQuery,
  useGetUserForRevisionListQuery,
  useHLPCategoriesQuery,
  useQAErrorCataloguesQuery,
  useQAProductFamiliesQuery,
  useRevisionFlagsQuery,
  useRevisionTechnicalPlacesQuery,
  useSQDCEventActionTypesQuery,
  type EABActionRevisionFragment,
  type EABEventRevisionFragment,
} from "@/graphql";
import type { EABEventActionConfigType } from "./EventActionHelper";
import { formatCodeName } from "@/modules/quality/helper/qaLanguage";

export type Revision = EABEventRevisionFragment | EABActionRevisionFragment;
export type RevisionChange = NonNullable<Revision["changes"]>[number];
export type RevisionTypeField = EABEventActionConfigType["SQDCEventActionTypeFields"][number];
type ErrorCatalogueRevisionItem = { id: number; [key: string]: unknown };
type ErrorCatalogueProductFamily = ErrorCatalogueRevisionItem & {
  errorCatalogs?: ErrorCatalogueRevisionItem[] | null;
};
type ErrorCatalogue = ErrorCatalogueRevisionItem & {
  productFamilies?: ErrorCatalogueRevisionItem[] | null;
};

export function useRevisionMetadata(
  revisions: ComputedRef<Revision[]>,
  eventActionTypeFields: ComputedRef<RevisionTypeField[]>,
  enabled: ComputedRef<boolean>,
  additionalDataNeeded?: ComputedRef<{
    userIds?: number[];
  }>,
) {
  const customFieldMap = computed(
    () => new Map(eventActionTypeFields.value.map((field) => [field.id, field])),
  );

  const userIds = computed(() => {
    const ids = new Set<number>();
    if (additionalDataNeeded?.value?.userIds) {
      additionalDataNeeded.value.userIds.forEach((id) => ids.add(id));
    }
    for (const revision of revisions.value) {
      addNumber(ids, revision.changedById);
      for (const change of revision.changes ?? []) {
        if (
          change.fieldType === SQDCEventRevisionFieldType.CREATOR ||
          change.fieldType === SQDCActionRevisionFieldType.ASSIGNEE ||
          isCustomRelation(change, customFieldMap.value, SQDCEventTypeFieldRelation.USER) ||
          isCustomFieldType(
            change,
            customFieldMap.value,
            SQDCEventActionTypeFieldType.PERCENT_SHARING,
          )
        ) {
          addValueIds(ids, change.oldValue);
          addValueIds(ids, change.newValue);
        }
      }
    }
    return [...ids];
  });

  const flagIds = computed(() => {
    const ids = new Set<number>();
    for (const revision of revisions.value) {
      for (const change of revision.changes ?? []) {
        if (change.fieldType === SQDCEventRevisionFieldType.FLAGS) {
          addValueIds(ids, change.oldValue);
          addValueIds(ids, change.newValue);
        }
      }
    }
    return [...ids];
  });

  const linkedEventIds = computed(() => {
    const ids = new Set<number>();
    for (const revision of revisions.value) {
      for (const change of revision.changes ?? []) {
        if (change.fieldType === SQDCEventRevisionFieldType.LINKAGE) {
          addValueIds(ids, change.oldValue);
          addValueIds(ids, change.newValue);
        }
      }
    }
    return [...ids];
  });

  const costCenterIds = computed(() =>
    collectCustomRelationIds(
      customFieldMap.value,
      revisions.value,
      SQDCEventTypeFieldRelation.COST_CENTER,
    ),
  );
  const technicalPlaceIds = computed(() =>
    collectCustomRelationIds(
      customFieldMap.value,
      revisions.value,
      SQDCEventTypeFieldRelation.TECHNICAL_PLACE,
    ),
  );
  const ideaCategoryIds = computed(() =>
    collectCustomRelationIds(
      customFieldMap.value,
      revisions.value,
      SQDCEventTypeFieldRelation.IDEA_CATEGORY,
    ),
  );
  const productFamilyIds = computed(() =>
    collectErrorCatalogueProductFamilyIds(customFieldMap.value, revisions.value),
  );
  const errorCatalogueIds = computed(() =>
    collectErrorCatalogueIds(customFieldMap.value, revisions.value),
  );

  const { result: userResult, loading: userLoading } = useGetUserForRevisionListQuery(
    () => ({ ids: userIds.value }),
    () => ({
      enabled: enabled.value && userIds.value.length > 0,
      fetchPolicy: "cache-first",
    }),
  );

  const users = computed(() => {
    const usersById = new Map(
      (userResult.value?.Users ?? []).map((user) => [user.id, user] as const),
    );
    for (const revision of revisions.value) {
      if (revision.changedBy != null) {
        usersById.set(revision.changedBy.id, revision.changedBy);
      }
    }
    return [...usersById.values()];
  });

  const { result: flagResult, loading: flagLoading } = useRevisionFlagsQuery(
    () => ({ ids: flagIds.value }),
    () => ({
      enabled: enabled.value && flagIds.value.length > 0,
    }),
  );

  const { result: linkedEventResult, loading: linkedEventLoading } =
    useEABRevisionLinkedEventsQuery(
      () => ({ ids: linkedEventIds.value }),
      () => ({
        enabled: enabled.value && linkedEventIds.value.length > 0,
        fetchPolicy: "cache-first",
      }),
    );
  const linkedEventTypeIds = computed(() => [
    ...new Set((linkedEventResult.value?.SQDCEvents ?? []).map((event) => event.typeId)),
  ]);
  const { result: linkedEventTypeResult, loading: linkedEventTypeLoading } =
    useSQDCEventActionTypesQuery(
      () => ({ ids: linkedEventTypeIds.value, includeDrafts: true }),
      () => ({
        enabled: enabled.value && linkedEventTypeIds.value.length > 0,
        fetchPolicy: "cache-first",
      }),
    );
  const linkedEventLabels = computed(() => {
    const prefixesByTypeId = new Map(
      (linkedEventTypeResult.value?.SQDCEventActionTypes ?? []).map((type) => [
        type.id,
        type.prefix,
      ]),
    );
    return new Map(
      (linkedEventResult.value?.SQDCEvents ?? []).flatMap((event) => {
        const label = getRevisionLinkedEventLabel(event, prefixesByTypeId);
        return label == null ? [] : [[event.id, label] as const];
      }),
    );
  });

  const { result: costCenterResult, loading: costCenterLoading } =
    useGetCostCentersForRainbowTileQuery(
      () => ({ ids: costCenterIds.value }),
      () => ({
        enabled: enabled.value && costCenterIds.value.length > 0,
      }),
    );

  const { result: technicalPlaceResult, loading: technicalPlaceLoading } =
    useRevisionTechnicalPlacesQuery(
      () => ({ ids: technicalPlaceIds.value }),
      () => ({
        enabled: enabled.value && technicalPlaceIds.value.length > 0,
      }),
    );
  const { result: ideaCategoryResult, loading: ideaCategoryLoading } = useHLPCategoriesQuery();
  const { result: productFamiliesResult, loading: productFamiliesLoading } =
    useQAProductFamiliesQuery(
      () => ({ ids: productFamilyIds.value }),
      () => ({
        enabled: enabled.value && productFamilyIds.value.length > 0,
      }),
    );
  const { result: errorCataloguesResult, loading: errorCataloguesLoading } =
    useQAErrorCataloguesQuery(
      () => ({ ids: errorCatalogueIds.value }),
      () => ({
        enabled: enabled.value && errorCatalogueIds.value.length > 0,
      }),
    );

  return {
    users,
    flags: computed(() => flagResult.value?.Flags ?? []),
    costCenters: computed(() => costCenterResult.value?.CostCenters ?? []),
    technicalPlaces: computed(() => technicalPlaceResult.value?.OGTechnicalPlaces ?? []),
    ideaCategories: computed(
      () =>
        ideaCategoryResult.value?.HLPCategories.filter((category) =>
          ideaCategoryIds.value.includes(category.id),
        ) ?? [],
    ),
    productFamilies: computed(() => productFamiliesResult.value?.QAProductFamilies ?? []),
    errorCatalogues: computed(() => errorCataloguesResult.value?.QAErrorCatalogues ?? []),
    linkedEventLabels,
    loading: computed(
      () =>
        userLoading.value ||
        flagLoading.value ||
        linkedEventLoading.value ||
        linkedEventTypeLoading.value ||
        costCenterLoading.value ||
        technicalPlaceLoading.value ||
        (ideaCategoryIds.value.length > 0 && ideaCategoryLoading.value) ||
        productFamiliesLoading.value ||
        errorCataloguesLoading.value,
    ),
    customFieldMap,
  };
}

export function getRevisionLinkedEventLabel(
  event: { id: number; oldId?: string | null; typeId: number },
  prefixesByTypeId: ReadonlyMap<number, string>,
) {
  if (event.oldId != null) {
    return event.oldId;
  }
  const prefix = prefixesByTypeId.get(event.typeId);
  return prefix == null ? undefined : `${prefix}${event.id}`;
}

function collectCustomRelationIds(
  customFieldMap: Map<number, RevisionTypeField>,
  revisions: Revision[],
  relation: SQDCEventTypeFieldRelation,
) {
  const ids = new Set<number>();
  for (const revision of revisions) {
    for (const change of revision.changes ?? []) {
      if (isCustomRelation(change, customFieldMap, relation)) {
        addValueIds(ids, change.oldValue);
        addValueIds(ids, change.newValue);
      }
    }
  }
  return [...ids];
}

function collectErrorCatalogueProductFamilyIds(
  customFieldMap: Map<number, RevisionTypeField>,
  revisions: Revision[],
) {
  const ids = new Set<number>();
  for (const revision of revisions) {
    for (const change of revision.changes ?? []) {
      if (isCustomRelation(change, customFieldMap, SQDCEventTypeFieldRelation.ERROR_CATALOGUE)) {
        addErrorCatalogueProductFamilyId(ids, change.oldValue);
        addErrorCatalogueProductFamilyId(ids, change.newValue);
      }
    }
  }
  return [...ids];
}

function collectErrorCatalogueIds(
  customFieldMap: Map<number, RevisionTypeField>,
  revisions: Revision[],
) {
  const ids = new Set<number>();
  for (const revision of revisions) {
    for (const change of revision.changes ?? []) {
      if (isCustomRelation(change, customFieldMap, SQDCEventTypeFieldRelation.ERROR_CATALOGUE)) {
        addErrorCatalogueIds(ids, change.oldValue);
        addErrorCatalogueIds(ids, change.newValue);
      }
    }
  }
  return [...ids];
}

function addErrorCatalogueProductFamilyId(ids: Set<number>, value: unknown) {
  const recordValue = parseRecordValue(value);
  if (recordValue == null) {
    return;
  }
  addNumber(ids, recordValue.productFamilyId);
}

function addErrorCatalogueIds(ids: Set<number>, value: unknown) {
  const recordValue = parseRecordValue(value);
  if (recordValue != null) {
    addValueIds(ids, recordValue.errorCatalogueIds);
    addNumber(ids, recordValue.errorCatalogueId);
    return;
  }
  addValueIds(ids, value);
}

export function formatErrorCatalogueRevisionValue(
  value: unknown,
  productFamilies: ErrorCatalogueProductFamily[],
  errorCatalogues: ErrorCatalogue[],
  locale: string,
  t: (key: string) => string,
  formatPlainValue: (value: unknown) => string,
) {
  const errorCatalogueValue = parseRecordValue(value);
  if (errorCatalogueValue == null) {
    return formatErrorCatalogueIds(value, productFamilies, errorCatalogues, locale, t);
  }

  const productFamilyId = toNumber(errorCatalogueValue.productFamilyId);
  const productFamily =
    productFamilyId == null
      ? undefined
      : productFamilies.find((entry) => entry.id === productFamilyId);
  const productFamilyLabel =
    productFamily == null
      ? productFamilyId == null
        ? ""
        : `Product Family ${productFamilyId}`
      : formatCodeName(productFamily, locale, t);

  const errorCatalogueIds = Array.isArray(errorCatalogueValue.errorCatalogueIds)
    ? errorCatalogueValue.errorCatalogueIds
    : [];
  const errorCatalogueLabels = errorCatalogueIds
    .map((entry) => {
      const id = toNumber(entry);
      if (id == null) {
        return formatPlainValue(entry);
      }
      const errorCatalogue = productFamilies
        .flatMap((family) => family.errorCatalogs ?? [])
        .find((catalogue) => catalogue.id === id);
      const loadedErrorCatalogue = errorCatalogues.find((catalogue) => catalogue.id === id);
      return errorCatalogue == null
        ? loadedErrorCatalogue == null
          ? `Error Catalogue ${id}`
          : formatCodeName(loadedErrorCatalogue, locale, t)
        : formatCodeName(errorCatalogue, locale, t);
    })
    .filter((entry) => entry !== "");

  return [productFamilyLabel, ...errorCatalogueLabels].filter((entry) => entry !== "").join("; ");
}

function formatErrorCatalogueIds(
  value: unknown,
  productFamilies: ErrorCatalogueProductFamily[],
  errorCatalogues: ErrorCatalogue[],
  locale: string,
  t: (key: string) => string,
) {
  const values = Array.isArray(value) ? value : [value];
  return values
    .map((entry) => {
      const id = toNumber(entry);
      if (id == null) {
        return String(entry);
      }
      const catalogue =
        errorCatalogues.find((candidate) => candidate.id === id) ??
        productFamilies
          .flatMap((family) => family.errorCatalogs ?? [])
          .find((candidate) => candidate.id === id);
      return catalogue == null ? `Error Catalogue ${id}` : formatCodeName(catalogue, locale, t);
    })
    .filter((entry) => entry !== "")
    .join(", ");
}

function isCustomRelation(
  change: RevisionChange,
  customFieldMap: Map<number, RevisionTypeField>,
  relation: SQDCEventTypeFieldRelation,
) {
  if (
    change.fieldType !== SQDCActionRevisionFieldType.CUSTOM_FIELD &&
    change.fieldType !== SQDCEventRevisionFieldType.CUSTOM_FIELD
  ) {
    return false;
  }
  const field = change.fieldId == null ? undefined : customFieldMap.get(change.fieldId);
  return field?.type === SQDCEventActionTypeFieldType.RELATION && field.relation === relation;
}

function isCustomFieldType(
  change: RevisionChange,
  customFieldMap: Map<number, RevisionTypeField>,
  fieldType: SQDCEventActionTypeFieldType,
) {
  if (
    change.fieldType !== SQDCActionRevisionFieldType.CUSTOM_FIELD &&
    change.fieldType !== SQDCEventRevisionFieldType.CUSTOM_FIELD
  ) {
    return false;
  }
  const field = change.fieldId == null ? undefined : customFieldMap.get(change.fieldId);
  return field?.type === fieldType;
}

function addValueIds(ids: Set<number>, value: unknown) {
  const recordValue = parseRecordValue(value);
  if (recordValue && "participants" in recordValue) {
    const participants = recordValue.participants;
    if (Array.isArray(participants)) {
      participants.forEach((participant) => {
        const participantValue = parseRecordValue(participant);
        if (participantValue && "userId" in participantValue) {
          addNumber(ids, participantValue.userId);
        }
      });
    }
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry) => addNumber(ids, entry));
    return;
  }
  addNumber(ids, value);
}

function parseRecordValue(value: unknown): Record<string, unknown> | undefined {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value !== "string" || value.trim() === "") {
    return undefined;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

function addNumber(ids: Set<number>, value: unknown) {
  const parsed = toNumber(value);
  if (parsed != null) {
    ids.add(parsed);
  }
}

function toNumber(value: unknown) {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
}
